/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";

type objectType = {
  [key: string]: any;
};

type IResolveParams = {
  phone_number_id: number;
  waba_id: number;
};

interface Props {
  appId: string;
  config_id: string;
  xfbml?: boolean;
  cookie?: boolean;
  version?: string;
  onLoginStart?: () => void;
  onLogoutSuccess?: () => void;
  onReject?: (reject: string | objectType) => void;
  onResolve: ({ phone_number_id, waba_id }: IResolveParams) => void;
  response_type?: string;
  children?: React.ReactNode;
  override_default_response_type?: boolean;
  extras?: {
    sessionInfoVersion: number;
  };
}

const SDK_URL: string = "https://connect.facebook.net/en_US/sdk.js";
const SCRIPT_ID: string = "facebook-jssdk";
const _window = window as any;

const FacebookLogin = ({
  appId,
  xfbml = true,
  cookie = true,
  version = "v19.0",
  onLoginStart,
  onReject = () => {
    /* noop */
  },
  onResolve,
  response_type = "code",
  config_id,
  override_default_response_type = true,
  extras = {
    sessionInfoVersion: 2,
  },
  children,
}: Props) => {
  const scriptNodeRef = useRef<HTMLElement>(null!);
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    !isSdkLoaded && load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSdkLoaded]);

  const sessionInfoListener = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== "https://www.facebook.com") return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          // if user finishes the Embedded Signup flow
          if (data.event === "FINISH") {
            const { phone_number_id, waba_id } = data.data;
            onResolve({ phone_number_id, waba_id });
          }
          // if user cancels the Embedded Signup flow
          else {
            /* noop */
          }
        }
      } catch {
        // Don't parse info that's not a JSON
      }
    },
    [onResolve]
  );

  useEffect(() => {
    window.addEventListener("message", sessionInfoListener);

    return () => {
      window.removeEventListener("message", sessionInfoListener);
    };
  }, [sessionInfoListener]);

  useEffect(
    () => () => {
      if (scriptNodeRef.current) scriptNodeRef.current.remove();
    },
    []
  );

  const insertSDKScript = useCallback((document: HTMLDocument, cb: () => void) => {
    const fbScriptTag = document.createElement("script");
    fbScriptTag.id = SCRIPT_ID;
    fbScriptTag.src = SDK_URL;

    fbScriptTag.onload = () => {};
    fbScriptTag.onerror = () => {};

    const scriptNode = document.getElementsByTagName("script")![0];
    if (scriptNode && scriptNode.parentNode) {
      scriptNode.parentNode.insertBefore(fbScriptTag, scriptNode);
    }

    cb();
  }, []);

  const checkIsExistsSDKScript = useCallback(() => {
    return !!document.getElementById(SCRIPT_ID);
  }, []);

  const initFbSDK = useCallback((config: objectType, document: HTMLDocument) => {
    const _window = window as any;

    _window.fbAsyncInit = function () {
      _window.FB && _window.FB.init({ ...config });
      setIsSdkLoaded(true);

      let fbRoot = document.getElementById("fb-root");
      if (!fbRoot) {
        fbRoot = document.createElement("div");
        fbRoot.id = "fb-root";
        document.body.appendChild(fbRoot);
      }
      scriptNodeRef.current = fbRoot as HTMLElement;
    };

    if (_window.FB) {
      _window.fbAsyncInit();
    }
  }, []);

  const handleResponse = useCallback((response: objectType) => {
    if (response?.authResponse) {
      const code = response.authResponse.code;
      void code;
    }
  }, []);

  const load = useCallback(() => {
    if (checkIsExistsSDKScript()) {
      setIsSdkLoaded(true);
    } else {
      insertSDKScript(document, () => {
        initFbSDK(
          {
            appId,
            cookie,
            xfbml,
            version,
          },
          document
        );
      });
    }
  }, [checkIsExistsSDKScript, insertSDKScript, initFbSDK, appId, cookie, xfbml, version]);

  const loginFB = useCallback(() => {
    if (!isSdkLoaded || isProcessing) {
      return;
    }

    if (!_window.FB) {
      load();
      onReject("Fb isn't loaded!");
      setIsProcessing(false);
    } else {
      setIsProcessing(true);
      onLoginStart && onLoginStart();

      _window.fbq &&
        _window.fbq("trackCustom", "WhatsAppOnboardingStart", {
          appId,
          feature: "whatsapp_embedded_signup",
        });

      // Aguardar um pouco para garantir que o FB.init() foi executado
      setTimeout(() => {
        _window.FB.login(handleResponse, {
          config_id,
          response_type,
          override_default_response_type,
          extras,
        });
      }, 100);
    }
  }, [
    isSdkLoaded,
    isProcessing,
    load,
    onReject,
    onLoginStart,
    appId,
    handleResponse,
    config_id,
    response_type,
    override_default_response_type,
    extras,
  ]);

  return <div onClick={loginFB}>{children}</div>;
};

export default memo(FacebookLogin);
